/**
 * Analytics Configuration — EVENT REGISTRY
 *
 * This is the SINGLE file that data engineers/analysts edit to add, rename,
 * or modify tracked events. No other code changes needed.
 *
 * Sections:
 *   1. pageViews        — Route path → page name mapping
 *   2. reduxActionEvents — Redux action type → Mixpanel event mapping
 *   3. clickEvents       — data-track attribute → Mixpanel event mapping
 *   4. settings          — Batching, environment, and endpoint config
 *
 * @module @ideoz/core/analytics/config
 */

import { AnalyticsConfig } from './analytics.types';

export const analyticsConfig: AnalyticsConfig = {
  // ============================================
  // SECTION 1: PAGE VIEW MAPPINGS
  // Analysts: Add new routes here to auto-track page views
  // ============================================
  pageViews: {
    '/': { page_name: 'landing' },
    '/landing': { page_name: 'landing' },
    '/login': { page_name: 'login' },
    '/register': { page_name: 'register' },
    '/forgot-password': { page_name: 'forgot_password' },
    '/reset-password': { page_name: 'reset_password' },
    '/conversation': { page_name: 'conversation' },
    '/conversation/:id': { page_name: 'conversation' },
    '/projects': { page_name: 'projects_list' },
    '/projects/:id': { page_name: 'project_details' },
    '/profile': { page_name: 'profile' },
    '/billing': { page_name: 'billing' },
    '/onboarding': { page_name: 'onboarding' },
    '/greeting': { page_name: 'greeting' },
    '/p/*': { page_name: 'published_prototype' },
  },

  // ============================================
  // SECTION 2: REDUX ACTION -> EVENT MAPPINGS
  // Analysts: Map any Redux action to a Mixpanel event
  // Format: 'slice/actionName' => { event details }
  // ============================================
  // reduxActionEvents: {
  //   // ---- Auth ----
  //   'app/setLoginState': {
  //     event_name: 'auth.login.succeeded',
  //     condition: (action) => action.payload === true,
  //     extractProperties: (_action, state) => ({
  //       user_id: (state.app as Record<string, unknown>)?.user
  //         ? ((state.app as Record<string, unknown>).user as Record<string, unknown>)?.id
  //         : undefined,
  //     }),
  //   },
  //   'app/logout': {
  //     event_name: 'auth.logout.clicked',
  //   },

  //   // ---- Projects (async thunks) ----
  //   'project/createProject/fulfilled': {
  //     event_name: 'project.created',
  //     extractProperties: (action) => ({
  //       project_id: (action.payload as Record<string, unknown>)?.id,
  //       project_name: (action.payload as Record<string, unknown>)?.name,
  //     }),
  //   },
  //   'project/deleteProject/fulfilled': {
  //     event_name: 'project.deleted',
  //     extractProperties: (action) => ({
  //       project_id: action.meta?.arg,
  //     }),
  //   },
  //   'project/updateProject/fulfilled': {
  //     event_name: 'project.updated',
  //     extractProperties: (action) => ({
  //       project_id: (action.meta?.arg as Record<string, unknown>)?.projectId,
  //     }),
  //   },
  //   'project/getUserProjectsWithPagination/fulfilled': {
  //     event_name: 'project.list.viewed',
  //     extractProperties: (action) => ({
  //       project_count: Array.isArray((action.payload as Record<string, unknown>)?.data)
  //         ? ((action.payload as Record<string, unknown>).data as unknown[]).length
  //         : undefined,
  //     }),
  //   },
  //   'project/uploadProjectFiles/fulfilled': {
  //     event_name: 'file.uploaded',
  //     extractProperties: (action) => ({
  //       project_id: (action.meta?.arg as Record<string, unknown>)?.projectId,
  //       file_count: Array.isArray((action.meta?.arg as Record<string, unknown>)?.files)
  //         ? ((action.meta?.arg as Record<string, unknown>).files as unknown[]).length
  //         : undefined,
  //       source: 'local',
  //     }),
  //   },

  //   // ---- Conversation Lifecycle ----
  //   'conversation/createNewConversation': {
  //     event_name: 'conversation.started',
  //     extractProperties: (_action, state) => ({
  //       has_project: !!(state.project as Record<string, unknown>)?.currentProjectId,
  //     }),
  //   },
  //   // ---- Conversation Messages ----
  //   'conversation/submitUserMessage': {
  //     event_name: 'conversation.message.sent',
  //     extractProperties: (action, state) => {
  //       const conv = state.conversation as Record<string, unknown>;
  //       const messages = conv?.messages;
  //       return {
  //         conversation_id: conv?.conversationId,
  //         message_length:
  //           typeof action.payload === 'string' ? (action.payload as string).length : undefined,
  //         is_first_message: Array.isArray(messages) ? messages.length <= 1 : undefined,
  //         mode: conv?.mode,
  //         llm_family: conv?.llmFamily,
  //         has_project: !!(state.project as Record<string, unknown>)?.currentProjectId,
  //       };
  //     },
  //   },
  //   'conversation/addAIResponse': {
  //     event_name: 'conversation.message.received',
  //     extractProperties: (_action, state) => {
  //       const conv = state.conversation as Record<string, unknown>;
  //       return {
  //         conversation_id: conv?.conversationId,
  //         mode: conv?.mode,
  //       };
  //     },
  //   },
  //   'conversation/updateMessageFeedback': {
  //     event_name: 'conversation.message.feedback',
  //     extractProperties: (action, state) => {
  //       const payload = action.payload as Record<string, unknown> | undefined;
  //       const conv = state.conversation as Record<string, unknown>;
  //       return {
  //         feedback_type: payload?.liked ? 'liked' : 'disliked',
  //         message_id: payload?.messageId,
  //         conversation_id: conv?.conversationId,
  //         project_id: (state.project as Record<string, unknown>)?.currentProjectId,
  //       };
  //     },
  //   },

  //   // ---- Conversation Mode ----
  //   'conversation/setMode': {
  //     event_name: 'conversation.mode.changed',
  //     extractProperties: (action, state) => ({
  //       to_mode: action.payload,
  //       conversation_id: (state.conversation as Record<string, unknown>)?.conversationId,
  //       has_project: !!(state.project as Record<string, unknown>)?.currentProjectId,
  //     }),
  //   },
  //   'conversation/setLlmFamily': {
  //     event_name: 'conversation.llm_family.changed',
  //     extractProperties: (action, state) => ({
  //       to_model: action.payload,
  //       conversation_id: (state.conversation as Record<string, unknown>)?.conversationId,
  //     }),
  //   },

  //   // ---- Prototyper Lifecycle ----
  //   'prototyper/setIsCreatingPrototype': {
  //     event_name: 'prototype.creation.started',
  //     condition: (action) => action.payload === true,
  //     extractProperties: (_action, state) => ({
  //       conversation_id: (state.conversation as Record<string, unknown>)?.conversationId,
  //       project_id: (state.project as Record<string, unknown>)?.currentProjectId,
  //       initiated_from_uncover: (state.conversation as Record<string, unknown>)
  //         ?.initiatedFromUncover,
  //     }),
  //   },
  //   'prototyper/setPrototypeUrl': {
  //     event_name: 'prototype.creation.completed',
  //     condition: (action) => !!action.payload,
  //     extractProperties: (action, state) => ({
  //       conversation_id: (state.conversation as Record<string, unknown>)?.conversationId,
  //       project_id: (state.project as Record<string, unknown>)?.currentProjectId,
  //       prototype_url: action.payload,
  //     }),
  //   },
  //   'prototyper/setPrototypeError': {
  //     event_name: 'prototype.creation.failed',
  //     condition: (action) => !!action.payload,
  //     extractProperties: (action, state) => ({
  //       conversation_id: (state.conversation as Record<string, unknown>)?.conversationId,
  //       error: action.payload,
  //     }),
  //   },
  //   'prototyper/clearPrototyper': {
  //     event_name: 'prototype.creation.cancelled',
  //     // Only fire when a prototype was actively being created before the clear.
  //     // clearPrototyper also runs as cleanup on conversation change / unmount,
  //     // which should NOT count as a cancellation.
  //     condition: (_action, prevState) =>
  //       !!(prevState?.prototyper as Record<string, unknown>)?.isCreatingPrototype,
  //     extractProperties: (_action, state) => ({
  //       conversation_id: (state.conversation as Record<string, unknown>)?.conversationId,
  //     }),
  //   },

  //   // ---- Project Mode ----
  //   'project/updateProjectMode/fulfilled': {
  //     event_name: 'project_details.mode_changed',
  //     extractProperties: (action) => ({
  //       project_id: (action.meta?.arg as Record<string, unknown>)?.projectId,
  //       to_mode: (action.meta?.arg as Record<string, unknown>)?.mode,
  //     }),
  //   },

  //   // ---- Project Details ----
  //   'project/getProject/fulfilled': {
  //     event_name: 'project_details.viewed',
  //     extractProperties: (action) => ({
  //       project_id: (action.payload as Record<string, unknown>)?.id,
  //       project_name: (action.payload as Record<string, unknown>)?.name,
  //     }),
  //   },
  //   'project/getProjectConversations/fulfilled': {
  //     event_name: 'project_details.conversations.loaded',
  //     extractProperties: (action) => ({
  //       conversation_count: Array.isArray(action.payload)
  //         ? (action.payload as unknown[]).length
  //         : undefined,
  //     }),
  //   },
  //   'project/getProjectAttachments/fulfilled': {
  //     event_name: 'project_details.attachments.loaded',
  //     extractProperties: (action) => ({
  //       attachment_count: Array.isArray(action.payload)
  //         ? (action.payload as unknown[]).length
  //         : undefined,
  //     }),
  //   },
  //   'project/deleteProjectAttachment/fulfilled': {
  //     event_name: 'file.deleted',
  //     extractProperties: (action) => ({
  //       file_id: action.meta?.arg,
  //       context: 'project',
  //     }),
  //   },

  //   // ---- App / User ----
  //   // NOTE: getUserProfile/fulfilled is NOT mapped here because it fires on every
  //   // profile refresh (credit updates, page loads, etc.), not just session start.
  //   // Session start is tracked via auth.login.succeeded (app/setLoginState).
  //   'app/setShowOnboarding': {
  //     event_name: 'onboarding.started',
  //     condition: (action) => action.payload === true,
  //   },

  //   // ---- Billing ----
  //   'app/requestUpgrade/fulfilled': {
  //     event_name: 'billing.upgrade.requested',
  //   },
  // },
  reduxActionEvents: {},

  // ============================================
  // SECTION 3: CLICK TRACKING MAPPINGS
  // Analysts: Define data-track attribute values and their events
  // In JSX: <button data-track="share_button">Share</button>
  // ============================================
  clickEvents: {
    // ---- UI / Navigation ----
    // share_button: { event_name: 'ui.share.clicked' },
    // help_docs: { event_name: 'ui.help_docs.clicked' },
    // social_links: { event_name: 'ui.social_links.clicked' },
    // sidebar_toggle: { event_name: 'conversation.sidebar.toggled' },
    // sidebar_new_chat: { event_name: 'conversation.sidebar.new_chat_clicked' },
    // sidebar_logo: { event_name: 'conversation.sidebar.logo_clicked' },
    // scroll_to_bottom: { event_name: 'conversation.scroll_to_bottom.clicked' },

    // ---- Auth ----
    // login_button: { event_name: 'auth.login.submitted' },
    // register_button: { event_name: 'auth.register.submitted' },
    // google_oauth: { event_name: 'auth.google.clicked' },
    // forgot_password_link: { event_name: 'auth.password.forgot_requested' },
    // auth_mode_switch: { event_name: 'conversation.auth_dialog.mode_switched' },

    // ---- Project ----
    // create_project: { event_name: 'conversation.project.dialog_opened' },
    // confirm_create_project: { event_name: 'conversation.project.created' },
    // delete_project: { event_name: 'project.delete.clicked' },

    // ---- Conversation Messages ----
    // message_like: { event_name: 'conversation.message.feedback.liked' },
    // message_dislike: { event_name: 'conversation.message.feedback.disliked' },
     copy_message: { event_name: 'user_copies_model_response' },
    // try_again: { event_name: 'conversation.message.retry' },
    // stop_generation: { event_name: 'conversation.streaming.cancelled' },
    // send_message: { event_name: 'conversation.message.sent' },

    // ---- Quick Actions ----
    // quick_action_chip: { event_name: 'conversation.quick_action.clicked' },
    // quick_actions_expand: { event_name: 'conversation.quick_action.expanded' },
    // quick_actions_collapse: { event_name: 'conversation.quick_action.collapsed' },

    // ---- Conversation Repetition ----
    // reframe_button: { event_name: 'conversation.repetition.reframed' },
    // skip_button: { event_name: 'conversation.repetition.skipped' },

    // ---- Prototype ----
    // generate_here: { event_name: 'prototype.generate_here.clicked' },
    // figjam_button: { event_name: 'prototype.figjam.clicked' },
    // generate_prototype: { event_name: 'conversation.mode.prototyper_from_uncover' },
    // back_to_uncover: { event_name: 'conversation.mode.back_to_uncover' },
    // prototype_banner: { event_name: 'prototype.panel.opened' },
    // prototype_close: { event_name: 'prototype.panel.closed' },
    // prototype_reopen: { event_name: 'prototype.panel.reopened' },
    // prototype_retry: { event_name: 'prototype.creation.retried' },
    prototype_publish: { event_name: 'prototype_shared' },
    // prototype_unpublish: { event_name: 'prototype.unpublished' },
    // prototype_copy_link: { event_name: 'prototype.link_copied' },
    // prototype_toggle_panel: { event_name: 'prototype.panel.conversation_toggled' },

    // ---- Design Insights ----
    design_insights_open: { event_name: 'design_insights_viewed' },
    //design_insights_close: { event_name: 'design_insights.panel.closed' },

    // ---- Drawing / FigJam ----
    // figjam_dialog_open: { event_name: 'drawing.figjam.dialog_opened' },
    // figjam_plugin_open: { event_name: 'drawing.figjam.plugin_opened' },
    // figjam_dialog_cancel: { event_name: 'drawing.figjam.dialog_cancelled' },
    // drawing_retry: { event_name: 'drawing.creation.retried' },

    // ---- Files ----
    // add_files: { event_name: 'file.picker.opened' },
    // file_remove: { event_name: 'file.deleted' },
    // upload_dialog_tab_switch: { event_name: 'file.upload_dialog.tab_switched' },
    // gdrive_sign_in: { event_name: 'file.gdrive.auth_clicked' },

    // ---- Account Creation (from conversation) ----
    // create_free_account: { event_name: 'conversation.account.create_clicked' },

    // ---- Banners ----
    // banner_action: { event_name: 'conversation.banner.action_clicked' },
    // banner_close: { event_name: 'conversation.banner.closed' },
    // credit_limit_action: { event_name: 'conversation.credit_limit_banner.action_clicked' },
    // credit_limit_dismiss: { event_name: 'conversation.credit_limit_banner.dismissed' },

    // ---- Billing / Upgrade ----
    upgrade_cta: { event_name: 'payment_initiated' },
    // upgrade_dialog_confirm: { event_name: 'conversation.upgrade_dialog.confirmed' },
    // upgrade_dialog_close: { event_name: 'conversation.upgrade_dialog.closed' },

    // ---- Project Details Page ----
    // pd_context_drawer_open: { event_name: 'project_details.context_drawer.opened' },
    // pd_context_drawer_close: { event_name: 'project_details.context_drawer.closed' },
    // pd_upload_dialog_open: { event_name: 'project_details.upload_dialog.opened' },
    // pd_conversation_click: { event_name: 'project_details.conversation.clicked' },
    // pd_hint_update_context: {
    //   event_name: 'project_details.need_smarter_help.update_context_clicked',
    // },
    // pd_hint_upload_files: {
    //   event_name: 'project_details.need_smarter_help.upload_files_clicked',
    // },
    // pd_upgrade_cta: { event_name: 'project_details.upgrade_cta.clicked' },
    // back_button: { event_name: 'navigation.back.clicked' },

    // ---- Design System / Brand Sync ----
    // design_system_open: { event_name: 'project_details.design_system.opened' },
    // design_system_close: { event_name: 'project_details.design_system.closed' },
    // design_system_save: { event_name: 'project_details.design_system.saved' },
    // design_system_reset: { event_name: 'project_details.design_system.reset' },
    // design_system_logo_upload: { event_name: 'project_details.design_system.logo_uploaded' },
    // design_system_logo_remove: { event_name: 'project_details.design_system.logo_removed' },
    // design_system_upgrade: { event_name: 'project_details.design_system.upgrade_clicked' },

    // ---- Google Drive (via UploadFilesDialog) ----
    // gdrive_disconnect: { event_name: 'project_details.gdrive.disconnected' },

    // ---- Sync Toast ----
    // sync_toast_minimize: { event_name: 'project_details.gdrive.toast_minimized' },
    // sync_toast_expand: { event_name: 'project_details.gdrive.toast_expanded' },
    // sync_toast_close: { event_name: 'project_details.gdrive.toast_closed' },

    // ---- Landing Page ----
    // landing_create_project: { event_name: 'landing.create_project_button_clicked' },
    // landing_what_is_project: { event_name: 'landing.what_is_project.opened' },
    //   ض2ص3ث4ق65غ7: { event_name: 'landing.project_clicked' },
    // landing_new_project_card: { event_name: 'landing.new_project_clicked' },
    // landing_upgrade_cta: { event_name: 'landing.upgrade_cta.clicked' },

    // ---- Project List Page ----
    // pl_project_card: { event_name: 'project_list.project_clicked' },
    // pl_new_project: { event_name: 'project_list.new_project_clicked' },
    // pl_create_project: { event_name: 'project_list.create_project_clicked' },
    // pl_what_is_project: { event_name: 'project_list.what_is_project_clicked' },
    // pl_upgrade_cta: { event_name: 'project_list.upgrade_cta_clicked' },
    // pl_video_open: { event_name: 'project_list.video_opened' },

    // ---- Onboarding ----
    // what_is_project_video: { event_name: 'onboarding.video.viewed' },
    // create_project_after_video: { event_name: 'onboarding.create_project_after_video.clicked' },
    // create_account_after_video: { event_name: 'onboarding.create_account_after_video.clicked' },
    // first_project_dialog_start: { event_name: 'onboarding.first_project.started' },

    // ---- Prototype Redirect (Plugin) ----
    // prototype_redirect_continue: {
    //   event_name: 'prototype.redirect_dialog.continue_to_platform',
    // },
    // prototype_redirect_close: { event_name: 'prototype.redirect_dialog.closed' },
  },

  // ============================================
  // SECTION 4: SETTINGS
  // ============================================
  settings: {
    batchSize: 10, // Send events in batches of 10
    batchIntervalMs: 5000, // Or every 5 seconds
    maxQueueSize: 100, // Max queued events before force-flush
    enableInDev: true, // Log events in development
    enabledEnvironments: ['development', 'production', 'staging'],
    endpoint: '/events', // Backend endpoint
    trackOnlyWhenLoggedIn: true, // Only send events when user is logged in
  },
};

// ============================================
// MANUAL ONBOARDING EVENTS (tracked via useTrackEvent in OnboardingTemplate)
// ============================================
// onboarding_started         — user lands on step 1 after registering
// onboarding_use_case_selected — user selects a use case (persona) on step 1 and clicks "continue"
//   properties: use_case (Freelance | Work | Personal | Education | Others)
// onboarding_intent_selected — user selects an intent on step 2 and clicks "continue"
//   properties: intent (ux-discovery | explore-problem | generate-solutions | not-sure)
// onboarding_completed      — user submits 1st prompt on step 3 or lands on homepage
//   properties: path ('first_prompt' | 'homepage'), intent (when path='homepage')
// tmp_chat_abandoned        — user exits conversation not in project without creating a project
//   properties: conversation_id, exit_source ('create_project_modal' | 'upgrade_dialog_closed')

// ============================================
// BACKWARD COMPATIBILITY MAPPING (reference only)
// Old numeric EventType → new object.action names
// ============================================
// signUp (1)            → auth.register.succeeded
// signIn (2)            → auth.login.succeeded
// logOut (3)            → auth.logout.clicked
// startSessions (4)     → auth.login.succeeded (merged — getUserProfile fires too often)
// ClickOnRegister       → auth.register.submitted
// ClickOnLogin          → auth.login.submitted
// ClickOnProfilePage    → page.viewed (page_name: profile)
// ClickOnCreateProject  → project.created
// ConfirmDeleteProject  → project.deleted
// OnUserMessage         → conversation.message.sent
// AfterFilesUploaded    → file.uploaded
